import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { WaitingRoom } from './WaitingRoom';
import { LiveKitVideoCall } from './LiveKitVideoCall';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Loader2, 
  AlertCircle, 
  Video
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { AvailabilityService } from '@/lib/services/availabilityService';
import { toLiveKitRoomName, type CallOutcome } from '@/lib/clinic/callSession';
import { toast } from 'sonner';

interface AvailabilityVideoCallProps {
  coachId: string;
}

type CallPhase = 'loading' | 'queue' | 'call' | 'error';

export function AvailabilityVideoCall({ coachId }: AvailabilityVideoCallProps) {
  const { t, language } = useLanguage();
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();
  
  const [phase, setPhase] = useState<CallPhase>('loading');
  const [error, setError] = useState<string | null>(null);
  const [coachData, setCoachData] = useState<any>(null);
  const [roomId, setRoomId] = useState<string>('');

  useEffect(() => {
    if (!coachId) {
      setError('Invalid coach ID');
      setPhase('error');
      return;
    }

    loadCoachAndAvailability();
  }, [coachId]);

  const loadCoachAndAvailability = async () => {
    try {
      setPhase('loading');
      
      // Load coach data
      const coachDoc = await getDoc(doc(db, 'users', coachId));
      if (!coachDoc.exists()) {
        setError(language === 'ar' ? 'المدرب غير موجود' : 'Coach not found');
        setPhase('error');
        return;
      }

      const coachInfo = coachDoc.data();
      setCoachData(coachInfo);

      // Automatically join the queue
      await autoJoinQueue();
    } catch (error) {
      console.error('Error loading coach data:', error);
      setError(language === 'ar' ? 'فشل في تحميل بيانات المدرب' : 'Failed to load coach data');
      setPhase('error');
    }
  };

  const autoJoinQueue = async () => {
    if (!currentUser || !userData) {
      setError(language === 'ar' ? 'يرجى تسجيل الدخول أولاً' : 'Please login first');
      setPhase('error');
      return;
    }

    try {
      const customerName = language === 'ar' 
        ? `${userData.firstNameAr || userData.firstName || 'مستفيد'} ${userData.lastNameAr || userData.lastName || ''}`.trim()
        : `${userData.firstNameEn || userData.firstName || 'Customer'} ${userData.lastNameEn || userData.lastName || ''}`.trim();

      await AvailabilityService.joinQueue(
        coachId, 
        currentUser.uid, 
        customerName,
        userData.phone
      );
      
      setPhase('queue');
    } catch (error: any) {
      console.error('Error joining queue:', error);
      setError(error.message || (language === 'ar' ? 'فشل في الانضمام للطابور' : 'Failed to join queue'));
      setPhase('error');
    }
  };

  const handleJoinQueue = async () => {
    // This function is kept for manual retry if auto-join fails
    await autoJoinQueue();
  };

  const handleCallStarted = (newRoomId: string) => {
    // Same LiveKit room name as the doctor's dashboard and the /clinic flow
    // (namat-{roomId}); the old coaching- prefix put the two sides in
    // different rooms (#47).
    setRoomId(toLiveKitRoomName(newRoomId));
    setPhase('call');
  };

  const handleCallEnded = (outcome?: CallOutcome) => {
    if (outcome && !outcome.connected) {
      // The doctor never joined: back to the waiting room instead of "completed"
      toast.info(language === 'ar'
        ? 'لم يكتمل الاتصال، عدت إلى غرفة الانتظار'
        : 'The call did not connect; you are back in the waiting room');
      setPhase('queue');
      return;
    }
    setError(language === 'ar' ? 'انتهت المكالمة بنجاح' : 'Call completed successfully');
    setPhase('error');
  };

  const handleLeaveQueue = () => {
    setError(language === 'ar' ? 'تم مغادرة غرفة الإنتظار' : 'Left the queue');
    setPhase('error');
  };

  const getCoachName = () => {
    if (!coachData) return language === 'ar' ? 'المدرب' : 'Coach';
    
    return language === 'ar'
      ? `${coachData.firstNameAr || coachData.firstName || 'مدرب'} ${coachData.lastNameAr || coachData.lastName || ''}`.trim()
      : `${coachData.firstNameEn || coachData.firstName || 'Coach'} ${coachData.lastNameEn || coachData.lastName || ''}`.trim();
  };


  // Loading phase
  if (phase === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/20 p-4">
        <Card className="w-full max-w-md mx-auto">
          <CardContent className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-lg font-medium">
              {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {language === 'ar' ? 'جاري تحميل بيانات المدرب والانضمام للطابور' : 'Loading coach information and joining queue'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error phase
  if (phase === 'error') {
    // Check if this is a success message (call completed) or actual error
    const isSuccess = error?.includes('انتهت المكالمة بنجاح') || error?.includes('Call completed successfully');
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-destructive/5 to-secondary/20 p-4">
        <Card className="w-full max-w-md mx-auto">
          <CardContent className="flex flex-col items-center justify-center py-8">
            {isSuccess ? (
              <Video className="h-12 w-12 text-green-500 mb-4" />
            ) : (
              <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
            )}
            {!isSuccess && (
              <h3 className="text-lg font-semibold mb-2">
                {language === 'ar' ? 'خطأ' : 'Error'}
              </h3>
            )}
            <p className="text-muted-foreground text-center mb-4">
              {error}
            </p>
            <Button onClick={() => navigate('/')}>
              {language === 'ar' ? 'العودة للرئيسية' : 'Go Home'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Queue phase
  if (phase === 'queue') {
    return (
      <WaitingRoom
        coachId={coachId}
        coachName={getCoachName()}
        coachAvatar={coachData?.profilePicture}
        onCallStarted={handleCallStarted}
        onLeaveQueue={handleLeaveQueue}
      />
    );
  }

  // Call phase - Using LiveKit
  if (phase === 'call') {
    const customerName = language === 'ar' 
      ? `${userData?.firstNameAr || userData?.firstName || 'مستفيد'} ${userData?.lastNameAr || userData?.lastName || ''}`.trim()
      : `${userData?.firstNameEn || userData?.firstName || 'Customer'} ${userData?.lastNameEn || userData?.lastName || ''}`.trim();

    return (
      <LiveKitVideoCall 
        roomName={roomId}
        participantName={customerName}
        userRole="customer"
        otherParticipantName={getCoachName()}
        onLeave={handleCallEnded}
        forceFullscreen={true}
        attempt={currentUser ? { coachId, customerId: currentUser.uid } : undefined}
      />
    );
  }

  // This should never be reached as we go directly from loading to queue
  return null;
}
